// apolo/src/lib/artifacts.js — Fase 1 (rail de Saídas).
//
// Helpers PUROS (sem React, sem fetch) — o ÚNICO lugar que entende o shape do
// tool_payload (contrato C1) e que deriva os Artifacts pro OutputsRail (C2).
// Jobs:
//   • buildToolPayload(toolMsg)        → JSON persistível quando um tool conclui (C1).
//   • buildPersistedMessages({...})    → lista de mensagens a persistir após um run.
//   • deriveArtifacts(messages)        → Artifact[] pro rail (de mensagens LIVE ou PERSISTIDAS).
//
// Realidade do Apolo: a única mutação de arquivo é `write_file` (conteúdo cheio,
// sem tool de edit por linha). Logo `additions` = nº de linhas do conteúdo e
// `deletions` = 0 no v1 (ver DECISIONS-code-chat-fase1.md · D8). run_command que
// imprime "N passed" vira um card de teste.

const SUMMARY_MAX = 500;
// E1 (Camada V) — fidelidade de contexto no reload. O `summary` do tool
// PERSISTIDO alimenta a reconstrução do histórico do modelo (agentHistory)
// após reload. Cap de 500 fazia o modelo ver MENOS que o run vivo (o loop usa
// MAX_TOOL_RESULT_CHARS=4000). Persistimos no MESMO teto → contexto remontado
// fiel. O rail/UI re-clampam no render.
const TOOL_RESULT_PERSIST_MAX = 4000;
// HRN3-T01 — persist a reasoning SUMMARY (not the full chain) so the
// ThinkingBlock survives reload instead of vanishing. Capped; a flag marks when
// it was trimmed so the UI can show an honest "reasoning truncated" affordance.
const REASONING_MAX = 2000;
const TEST_RE = /\b\d+\s+(?:passed|passing|pass)\b/i;
// Status terminais (concluídos de algum jeito). Persistimos só esses — nunca um
// turno 'running'/indefinido (que viraria 'done' por default e mentiria sucesso).
const TERMINAL_STATUS = new Set(['done', 'denied', 'error', 'aborted']);

export function countLines(text) {
  if (text == null) return 0;
  const t = String(text).replace(/\n+$/, '');
  return t === '' ? 0 : t.split('\n').length;
}

function exitCodeOf(result) {
  const m = String(result ?? '').match(/^exitCode:\s*(-?\d+)/);
  return m ? Number(m[1]) : null;
}

function cleanSummary(result, max = SUMMARY_MAX) {
  return String(result ?? '')
    .replace(/^exitCode:\s*-?\d+\n?/, '')
    .trim()
    .slice(0, max);
}

// C1 — payload persistível a partir de uma tool message concluída (live).
export function buildToolPayload(msg = {}) {
  const tool = msg.tool_name || 'tool';
  const args = msg.tool_args || {};
  const payload = { tool, status: msg.status || 'done' };
  if (typeof msg.duration_ms === 'number') payload.duration_ms = msg.duration_ms;
  if (args.path) payload.path = String(args.path);
  if (tool === 'write_file') {
    payload.additions = countLines(args.content);
    payload.deletions = 0;
  }
  if (tool === 'run_command') {
    const code = exitCodeOf(msg.result);
    if (code != null) payload.exit_code = code;
  }
  // E1 — persiste o result no teto do loop (fidelidade no reload), não em 500.
  const summary = cleanSummary(msg.result, TOOL_RESULT_PERSIST_MAX);
  if (summary) payload.summary = summary;
  return payload;
}

// E2-WS3-T01 — causa-raiz persistível de um turno FALHO (null quando ok). Puro;
// vive aqui (e não no SessionWorkspace, que está no teto do ratchet dp-loc).
export function turnErrorOf(agentResult) {
  if (!agentResult || agentResult.ok !== false) return null;
  return agentResult.error || agentResult.stopReason || 'falha desconhecida';
}

// GLM forense (2026-08, Pedro): um turno com GLM lento estourava o timeout do
// provider (AbortSignal.timeout server-side / deadline por chamada) e a string
// CRUA "aborted" subia via finishFollowupFailed → "⚠️ Turno falhou: aborted",
// opaca e sem próximo passo. Mapeia erros com CARA de abort/timeout numa mensagem
// AMIGÁVEL e acionável (o raw fica em metadata.error pra telemetria). Puro/testável.
// Stop DO USUÁRIO não chega aqui (vai por blockerFallbackNotice→silêncio), então
// "aborted" neste ponto é sempre um corte por lentidão/timeout, não um Stop.
const ABORT_RE = /\b(abort(ed|ado)?|operation was aborted|cancell?ed)\b/i;
const TIMEOUT_RE = /(time[\sd-]*out|timeout|deadline|idle \d+ms|ttft|wall-?clock|sem primeiro token)/i;
export function friendlyTurnError(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return 'Turno falhou (motivo desconhecido). O estado foi preservado — tente de novo.';
  if (TIMEOUT_RE.test(s) || ABORT_RE.test(s)) {
    return 'Turno interrompido: o modelo demorou além do limite e a chamada foi cortada (timeout do provider). '
      + 'Tente de novo — se o modelo estiver lento (ex.: GLM), troque de modelo ou quebre o passo em partes menores.';
  }
  return `Turno falhou: ${s}`;
}

// Monta a lista de mensagens a persistir após um run (ordem: user → tools →
// agent final). Mantém o SessionWorkspace burro: ele só itera e faz POST.
// Cada `rec` é { tool_name, tool_args, content, status, result, duration_ms }.
export function buildPersistedMessages({ userText, toolRecords = [], finalText, reasoning, includeUser = true, error = null } = {}) {
  const out = [];
  // includeUser=false (auditoria 2026-07-02): a mensagem do usuário é persistida
  // no DISPATCH (não só no fim do turno) pra sobreviver a um reload no meio; os
  // sites de fim de turno então a omitem pra não duplicar a linha.
  if (userText && includeUser) out.push({ role: 'user', content: userText });
  for (const rec of toolRecords) {
    if (!rec || !rec.tool_name || !TERMINAL_STATUS.has(rec.status)) continue;
    out.push({
      role: 'tool',
      tool_name: rec.tool_name,
      content: rec.content || rec.tool_name,
      metadata: buildToolPayload(rec),
    });
  }
  if (finalText) {
    const agentMsg = { role: 'agent', content: finalText };
    // HRN3-T01 — carry a capped reasoning summary in metadata (survives reload
    // via tool_payload; ConversationFeed reads metadata.reasoning as a fallback).
    const r = typeof reasoning === 'string' ? reasoning.trim() : '';
    if (r) {
      const truncated = r.length > REASONING_MAX;
      agentMsg.metadata = {
        reasoning: truncated ? `${r.slice(0, REASONING_MAX)}…` : r,
        reasoning_truncated: truncated,
      };
    }
    out.push(agentMsg);
  }
  // FASE-11 E2-WS3-T01 — turno FALHO deixa registro persistente: antes, o erro era
  // live-only (setError/pushMsg) e um reload mostrava o turno como se tivesse
  // simplesmente... parado. role:'system' + metadata.kind='turn_error' → o
  // ConversationFeed já renderiza system rows; o kind permite estilizar/filtrar.
  if (error) {
    const errText = String(error).slice(0, 500);
    out.push({
      role: 'system',
      content: `⚠️ ${friendlyTurnError(errText)}`,
      metadata: { kind: 'turn_error', error: errText },
    });
  }
  return out;
}

// C2 — Artifact[] pro rail, de mensagens LIVE ou PERSISTIDAS. Pura.
export function deriveArtifacts(messages = []) {
  const out = [];
  (messages || []).forEach((m, i) => {
    if (!m || m.role !== 'tool') return;
    const p = m.metadata && typeof m.metadata === 'object' ? m.metadata : null;
    const tool = p?.tool || m.tool_name;
    const status = p?.status ?? m.status ?? null;
    const durationMs = p?.duration_ms ?? m.duration_ms ?? null;
    const id = m.id || `art-${i}`;

    // Só deriva artefato de SUCESSO — write negado/abortado/erro NÃO vira card de
    // arquivo, e "N passed" de um run com exit≠0 NÃO vira card de teste (CodeRabbit).
    if (status !== 'done') return;

    if (tool === 'write_file') {
      const path = p?.path ?? m.tool_args?.path;
      if (!path) return;
      out.push({
        id,
        kind: 'file',
        tool,
        path: String(path),
        additions: p ? p.additions ?? 0 : countLines(m.tool_args?.content),
        deletions: p ? p.deletions ?? 0 : 0,
        status,
        durationMs,
      });
    } else if (tool === 'run_command') {
      const exit = p ? p.exit_code : exitCodeOf(m.result);
      const summary = p?.summary ?? cleanSummary(m.result);
      if (exit === 0 && TEST_RE.test(summary)) {
        out.push({ id, kind: 'test', tool, path: null, summary: summary.slice(0, 120), status, durationMs });
      }
    }
  });
  return out;
}
